/**
 * 브랜드 — 고객사 환경 설정의 주 색과 사내 글꼴을 템플릿에 입힌다.
 *
 * **둘 다 렌더 결과(HTML)가 아니라 받는 쪽이 문서에 한 번 거는 스타일시트다.** 템플릿을 그릴 때마다
 * `<style>` 을 함께 넣으면 항목이 바뀔 때마다 다시 파싱하고(몇 달씩 도는 화면에서 쌓인다), 색 하나를
 * 바꾸려고 모든 템플릿 콘텐츠의 지문이 바뀐다. 플레이어와 콘솔 미리보기가 같은 함수로 같은 시트를
 * 만들어 건다.
 *
 * **CSS 변수를 쓰지 않는다.** 납품 기기(안드로이드 5.1, Chromium 39)가 모른다(`styles.ts` 머리말).
 * 그래서 기본 파랑(#0a84ff)을 쓰는 규칙을 하나하나 같은 선택자로 다시 적는다 — 기본 시트 뒤에 걸리므로
 * 같은 무게에서 이긴다.
 */
import { DEFAULT_TEMPLATE_FONT, TEMPLATE_FONTS } from './fonts.js';
/** 사내 글꼴을 고른 템플릿의 글꼴 값(`payload.font`). 파일은 고객사가 환경 설정에서 올린다. */
export const BRAND_FONT_ID = 'brand';
/** 사내 글꼴의 `@font-face` 이름. 기기에 같은 이름의 다른 글꼴이 있어도 섞이지 않게 앞에 붙인다. */
export const BRAND_FONT_FAMILY = 'SO Brand';
/** 사내 글꼴을 못 읽었을 때(아직 안 받았거나 올린 적이 없다) 물러날 곳 — 기본 글꼴. */
export function brandFontStack() {
    const fallback = TEMPLATE_FONTS[DEFAULT_TEMPLATE_FONT];
    return `'${BRAND_FONT_FAMILY}', '${fallback.family}', ${fallback.fallback}`;
}
/** 올린 글꼴 파일의 형식 — `@font-face` 의 `format()` 에 그대로 들어간다. */
export function brandFontFormat(mime) {
    switch (mime) {
        case 'font/woff2':
            return 'woff2';
        case 'font/woff':
            return 'woff';
        case 'font/otf':
            return 'opentype';
        default:
            return 'truetype';
    }
}
/**
 * 사내 글꼴의 `@font-face`.
 *
 * 올린 파일은 하나지만 템플릿은 500·700·800 을 쓴다. 굵기마다 같은 파일을 걸어 두면 브라우저가
 * 그 파일을 그대로 쓴다 — 하나만 걸면 굵은 글자를 흉내 내어(가짜 굵게) 획이 뭉개진다. 범위 표기
 * (`100 900`)는 옛 웹뷰가 모른다.
 */
export function brandFontFaceCss(url, mime) {
    const format = brandFontFormat(mime);
    return [400, 500, 700, 800]
        .map((weight) => `@font-face{font-family:'${BRAND_FONT_FAMILY}';src:url('${cssString(url)}') format('${format}');` +
        `font-weight:${weight};font-style:normal;font-display:block}`)
        .join('\n');
}
/** 사내 글꼴을 고른 무대의 글꼴 — 기본 시트(`TEMPLATE_CSS`)에 함께 실린다. */
export function brandFontRule() {
    return `.so-font-${BRAND_FONT_ID} .so-tpl-canvas { font-family: ${brandFontStack()}; }`;
}
const HEX = /^#([0-9a-f]{6})$/i;
/** `#RRGGBB` 를 비율만큼 어둡게. 그라데이션의 끝 색이다. */
function darken(hex, ratio) {
    const value = Number.parseInt(hex.slice(1), 16);
    const channel = (shift) => Math.round(((value >> shift) & 0xff) * (1 - ratio))
        .toString(16)
        .padStart(2, '0');
    return `#${channel(16)}${channel(8)}${channel(0)}`;
}
/**
 * 주 색을 바꾸는 시트. 색이 없거나 형식이 아니면 빈 문자열 — 기본 파랑 그대로다.
 *
 * 바꾸는 자리는 기본 파랑이 "이 화면의 색" 으로 쓰인 곳뿐이다: 티커 머리표, 배지, 공지 아이콘,
 * 날씨·일정·혜택 카드, 그리고 색 이름 `blue` 의 타일·점·막대. 무재해 현황(2026-11-03)은 달성률 칸·제목 아래 아이콘·시작 전
 * D-day·진행 막대. **판정 색은 건드리지 않는다** — 긴급(주황)·증감(초록·빨강)은 뜻이 있는 색이라 브랜드가 덮으면
 * 읽는 법이 무너진다. 무재해의 진행 중 D-day(초록)·달성(노랑)도 그렇다.
 */
export function templateBrandCss(accent) {
    if (!accent || !HEX.test(accent))
        return '';
    const color = accent.toLowerCase();
    const deep = darken(color, 0.3);
    const solid = [
        '.so-tpl__ticker-label',
        '.so-badge',
        '.so-tone-blue',
        '.so-dot-blue',
        '.so-bar-blue',
        '.so-fill-blue',
        '.so-zero__kpi--accent',
    ];
    const gradient = ['.so-ann__weather', '.so-promo__visual', '.so-promo__benefit-icon', '.so-sched__today'];
    return [
        `${solid.join(', ')} { background: ${color}; }`,
        // 경보 머리표는 주황 그대로 — 위 규칙보다 뒤에 다시 적는다.
        `.so-tpl__footer--alert .so-tpl__ticker-label { background: #ff9f0a; }`,
        `.so-badge--glass { background: rgba(255,255,255,0.22); }`,
        `.so-ann__meta-item .so-i, .so-zero__meta-item .so-i, .so-zero__dday--before .so-zero__dday-value { color: ${color}; }`,
        // 무재해 진행 막대 — 브랜드 색 한 계열로(어두운 색에서 그 색으로). 기본의 파랑→초록처럼 초록으로 끝내면 빨강 계열 색은
        // 가운데가 탁한 갈색이 된다.
        `.so-zero__fill { background: ${color}; background: linear-gradient(90deg, ${deep}, ${color}); }`,
        `${gradient.join(', ')} { background: ${color}; background: linear-gradient(135deg, ${color}, ${deep}); }`,
    ].join('\n');
}
/** CSS 문자열 안에 넣을 주소 — 따옴표·역슬래시·줄바꿈이 시트를 끊지 못하게. */
function cssString(value) {
    return value.replace(/\\/g, '\\\\').replace(/'/g, "\\'").replace(/[\n\r]/g, '');
}
/**
 * 머리글의 장소 글(2026-11-03) — 템플릿 머리글 왼쪽, 브랜드 이름 옆에 적는 글을 무엇으로 할지. 환경 설정의 `brand.place`.
 *
 * - `name` 화면 이름(기본 — 그 전과 같다, 이름이 비면 위치)
 * - `location` 화면 위치(화면 정보의 "위치", 비면 적지 않는다)
 * - `custom` 모든 화면에 같은 글
 * - `none` 적지 않는다
 *
 * 화면 이름은 콘솔에서 화면을 가려내는 관리용 이름이라("새 화면 (안드로이드 962×541)") 공공 화면에 그대로 나가면 어색하다.
 */
export const HEADER_PLACE_MODES = ['name', 'location', 'custom', 'none'];
export const DEFAULT_HEADER_PLACE = { mode: 'name', text: '' };
/** 직접 입력 글의 길이 상한 — 머리글 한 줄에 브랜드 이름과 나란히 들어가야 한다. */
export const HEADER_PLACE_MAX = 40;
/** 이 화면의 머리글 장소 글 — 서버(화면에 내려 줄 값)와 콘솔(미리보기)이 같은 셈을 쓴다. */
export function headerPlace(setting, display) {
    const name = (display.name ?? '').trim();
    const location = (display.location ?? '').trim();
    switch (setting?.mode ?? 'name') {
        case 'location':
            return location;
        case 'custom':
            return (setting?.text ?? '').trim();
        case 'none':
            return '';
        default:
            return name || location;
    }
}
//# sourceMappingURL=brand.js.map